import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtUsd, STATUS_COLORS } from "@/lib/nexus";
import { ArrowLeft, MapPin, Globe, Server, Fingerprint, ShieldAlert, Loader2, Radio, Building2 } from "lucide-react";

// Fix Leaflet default icon paths (unused with divIcon but kept for popups)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const makeIcon = (color) =>
  L.divIcon({
    className: "nexus-agent-marker",
    html: `<div style="width:14px;height:14px;border-radius:999px;background:${color};box-shadow:0 0 12px ${color},0 0 0 2px #040914;"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

function InfoRow({ label, value, icon: Icon, color = "text-nexus-text" }) {
  return (
    <div className="flex justify-between items-baseline gap-3 py-1.5 border-b border-nexus-border/60">
      <span className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-3 flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </span>
      <span className={`text-[12px] font-mono ${color} text-right break-all max-w-[220px]`}>{value}</span>
    </div>
  );
}

export default function StreetView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [agent, setAgent] = useState(null);
  const [neighbors, setNeighbors] = useState([]);

  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const targetMarkerRef = useRef(null);
  const neighborLayerRef = useRef(null);
  const haloRef = useRef(null);

  useEffect(() => {
    api.get(`/agents/${id}`).then((r) => {
      setAgent(r.data.agent);
      api.get(`/geo/agents?region=${r.data.agent.region}`).then((res) => setNeighbors(res.data.items));
    });
  }, [id]);

  // Initialize Leaflet imperatively — StrictMode-safe with proper cleanup
  useEffect(() => {
    if (!agent || !mapDivRef.current) return;
    const el = mapDivRef.current;
    // Guard against double-init from StrictMode
    if (el._leaflet_id) return;

    const pos = [agent.physical.lat, agent.physical.lng];
    const map = L.map(el, {
      center: pos,
      zoom: 4,
      scrollWheelZoom: true,
      zoomControl: true,
      preferCanvas: false,
      attributionControl: true,
    });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);

    const color = STATUS_COLORS[agent.status] || "#00F0FF";
    const marker = L.marker(pos, { icon: makeIcon(color) }).addTo(map);
    marker.bindPopup(
      `<div style="font-family:JetBrains Mono,monospace;font-size:11px;color:#0a1122;">
         <div style="font-family:Rajdhani,sans-serif;font-weight:600;letter-spacing:2px;text-transform:uppercase;font-size:12px">${agent.name}</div>
         <div>${agent.physical.street_address}</div>
         <div>${agent.physical.city} · ${agent.physical.country}</div>
       </div>`,
    );

    const halo = L.circle(pos, {
      radius: 20000,
      color, fillColor: color, fillOpacity: 0.08, weight: 1.2,
    }).addTo(map);
    haloRef.current = halo;

    const neighborLayer = L.layerGroup().addTo(map);
    neighborLayerRef.current = neighborLayer;

    mapRef.current = map;
    targetMarkerRef.current = marker;

    // Cleanup on unmount
    return () => {
      map.remove();
      mapRef.current = null;
      targetMarkerRef.current = null;
      neighborLayerRef.current = null;
      haloRef.current = null;
    };
  }, [agent]);

  // Sync neighbors onto the map
  useEffect(() => {
    const layer = neighborLayerRef.current;
    if (!layer || !agent) return;
    layer.clearLayers();
    neighbors.filter((n) => n.id !== agent.id).forEach((n) => {
      const m = L.marker([n.lat, n.lng], { icon: makeIcon((STATUS_COLORS[n.status] || "#00F0FF") + "CC") });
      m.bindPopup(
        `<div style="font-family:JetBrains Mono,monospace;font-size:11px;color:#0a1122;">
           <div style="font-family:Rajdhani,sans-serif;font-weight:600;letter-spacing:2px;text-transform:uppercase;font-size:12px">${n.name}</div>
           <div>${n.framework} · ${n.chain}</div>
           <a href="/street/${n.id}" style="color:#0891b2;">open →</a>
         </div>`,
      );
      m.addTo(layer);
    });
  }, [neighbors, agent]);

  const setZoom = (z) => {
    const map = mapRef.current;
    if (!map || !agent) return;
    map.flyTo([agent.physical.lat, agent.physical.lng], z, { duration: 1.2 });
    const halo = haloRef.current;
    if (halo) {
      const r = z >= 15 ? 60 : z >= 10 ? 800 : 20000;
      halo.setRadius(r);
    }
  };

  if (!agent) return (
    <div className="p-8 flex items-center justify-center text-nexus-text-2">
      <Loader2 className="animate-spin" size={20} />
    </div>
  );

  const p = agent.physical;
  const color = STATUS_COLORS[agent.status];
  const riskColor = p.physical_risk_score >= 60 ? "#FF3366" :
                    p.physical_risk_score >= 30 ? "#FFB000" : "#00F0FF";

  return (
    <div className="flex flex-col h-[calc(100vh-48px)]">
      {/* Top bar */}
      <div className="border-b border-nexus-border bg-nexus-panel/60 backdrop-blur px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <button
            data-testid="street-back-btn"
            onClick={() => nav(-1)}
            className="text-[11px] font-mono uppercase tracking-widest text-nexus-text-2 hover:text-nexus-cyan flex items-center gap-1 shrink-0"
          >
            <ArrowLeft size={12} /> BACK
          </button>
          <div className="min-w-0">
            <div className="panel-title">PHYSICAL DRILL-DOWN // STREET-LEVEL INTEL</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
              <h1 className="font-rajdhani font-bold text-xl uppercase tracking-widest truncate">{agent.name}</h1>
              <span className="hash">{agent.short_address}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="zoom-region" onClick={() => setZoom(5)} className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-2 border border-nexus-border hover:text-nexus-cyan hover:border-nexus-cyan px-2 py-1">Region</button>
          <button data-testid="zoom-city" onClick={() => setZoom(11)} className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-2 border border-nexus-border hover:text-nexus-cyan hover:border-nexus-cyan px-2 py-1">City</button>
          <button data-testid="zoom-street" onClick={() => setZoom(17)} className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan hover:bg-nexus-cyan/10 px-2 py-1">Street</button>
        </div>
      </div>

      {/* Body: map + intel panel */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative">
          <div ref={mapDivRef} style={{ height: "100%", width: "100%", background: "#040914" }} data-testid="street-map" />
          <div className="absolute top-3 left-3 panel px-3 py-2 text-[11px] font-mono uppercase tracking-widest max-w-md z-[1000] pointer-events-none">
            <div className="text-nexus-text-3">Physical Address</div>
            <div className="text-nexus-cyan text-[13px] mt-0.5 normal-case tracking-normal font-outfit">{p.street_address}</div>
            <div className="text-nexus-text-2 mt-0.5 normal-case tracking-normal">{p.city} · {p.country}</div>
          </div>
        </div>

        {/* Right intel panel */}
        <aside className="w-[380px] border-l border-nexus-border bg-nexus-panel/70 backdrop-blur overflow-auto p-4 space-y-4">
          <div className="border border-nexus-border p-3">
            <div className="flex justify-between items-baseline">
              <div className="panel-title"><Fingerprint size={12} className="inline mr-1" /> Physical Risk</div>
              <div className="font-rajdhani font-bold text-3xl" style={{ color: riskColor }} data-testid="physical-risk-score">{p.physical_risk_score}</div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-3 mt-1">/ 100</div>
            <div className="mt-3 space-y-1">
              {p.physical_risk_factors.length === 0 ? (
                <div className="text-[11px] text-nexus-text-2 italic">No elevated physical-layer risk factors.</div>
              ) : p.physical_risk_factors.map((f) => (
                <div key={f} className="text-[11px] font-mono text-nexus-text-2 flex items-start gap-1.5">
                  <ShieldAlert size={10} className="mt-0.5 shrink-0" style={{ color: riskColor }} />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="panel-title mb-2"><Globe size={12} className="inline mr-1" /> Network Attribution</div>
            <InfoRow label="IP" value={<span className="text-nexus-cyan">{p.ip}</span>} icon={Radio} />
            <InfoRow label="ASN" value={p.asn} />
            <InfoRow label="ISP" value={p.isp} />
          </div>

          <div>
            <div className="panel-title mb-2"><Server size={12} className="inline mr-1" /> Device Fingerprint</div>
            <InfoRow label="Hosting" value={p.hosting_provider} />
            <InfoRow label="OS" value={p.device_os} />
            <InfoRow label="Runtime" value={p.runtime} />
          </div>

          <div>
            <div className="panel-title mb-2"><MapPin size={12} className="inline mr-1" /> Geo</div>
            <InfoRow label="City" value={p.city} />
            <InfoRow label="Country" value={p.country} />
            <InfoRow label="Coords" value={`${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`} />
          </div>

          <div>
            <div className="panel-title mb-2"><Building2 size={12} className="inline mr-1" /> On-Chain Snapshot</div>
            <InfoRow label="Framework" value={agent.framework} />
            <InfoRow label="Chain" value={agent.chain} />
            <InfoRow label="Balance" value={fmtUsd(agent.balance_usd)} />
            <InfoRow label="Tx count" value={agent.tx_count.toLocaleString()} />
            <InfoRow label="On-chain risk" value={agent.risk_score} color={agent.status === "critical" ? "text-nexus-rose" : agent.status === "watch" ? "text-nexus-amber" : "text-nexus-cyan"} />
          </div>

          <Link
            to={`/agent/${agent.id}`}
            className="block text-center text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/50 hover:bg-nexus-cyan/10 px-2 py-2"
            data-testid="street-view-full-agent"
          >
            Open Full Agent Dossier →
          </Link>
        </aside>
      </div>
    </div>
  );
}
